import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useRef } from "react";
import { message } from "antd";

import "shared/assets/styles/components/demobot-control-page.css";
import { useServerConnection } from "shared/contexts/ServerConnectionContext";
import LogViewer from "shared/components/ui/LogViewer";
import { useUserData } from "shared/contexts/UserDataProvider";
import RobotPeerVideoViewer from "features/robot-teleop/components/robot-video-viewer";
import { RobotPeerConnectionProvider } from "features/robot-teleop/contexts/RobotConnectionContext";
import { usePageTitle } from "shared/contexts/PageTitleContext";

const RobotGroupMonitorPage = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const { id } = useParams();
  // console.log("robot group id: ", id);

  const { updatePageTitle } = usePageTitle();

  // set page title
  useEffect(() => {
    updatePageTitle(`${id} | Monitor`);
  }, [id]);

  const {
    isOpen: signal_ready,
    message: signal_message,
    sendMessage: signal_send,
    subscribe,
    error: signal_Error,
    reconnectCountDown,
  } = useServerConnection();

  const { userDataState, getCameraPeers } = useUserData();
  const robotGroups = userDataState.robotGroups;

  const robotGroupInfo = robotGroups[id];

  // console.log("robot group info: ", robotGroupInfo);

  // const camera1PeerId = robotGroupInfo?.camera1PeerId;
  // const camera2PeerId = robotGroupInfo?.camera2PeerId;
  // const controlPeerId = robotGroupInfo?.controlPeerId;
  // const cameraPeers = getCameraPeers(robotGroupInfo);
  const peerConnections = robotGroupInfo?.connections;

  // console.log("peers: ", peerConnections);

  const audioAlarm = useRef(null);

  const [connectionLogRecords, setConnectionLogRecords] = useState([]);
  const logRecordsRef = useRef([]);
  const logDisplaySize = 10;
  const maxLogRecords = logDisplaySize; // Limit log records to prevent memory growth

  const [robotStatus, setRobotStatus] = useState(null);
  const [robotErrorInfo, setRobotErrorInfo] = useState(null);
  const [missionStatus, setMissionStatus] = useState(null);

  const robotInfo = { robot_id: id, icon: "/images/demobot2.png", name: id };
  const connectionsStatus = {
    ServerConnectionStatus: signal_ready ? "connected" : "disconnected",
    PeerConnectionStatus: "",
    RobotOnlineStatus: "",
  };

  // Throttle control for addLogRecord
  const lastLogTimeRef = useRef(0);
  const logUpdateTimerRef = useRef(null);
  const logThrottleDelay = 1000; // 100ms throttle delay

  const addLogRecord = useCallback(
    (msg) => {
      const trimmedMsg = msg.length > 100 ? msg.slice(0, 100) + "..." : msg;
      const time = new Date();
      const logRecord = { Time: time, Message: trimmedMsg };

      logRecordsRef.current = [...logRecordsRef.current, logRecord];
      if (logRecordsRef.current.length > maxLogRecords) {
        logRecordsRef.current = logRecordsRef.current.slice(-maxLogRecords);
      }

      const lastLogTime = lastLogTimeRef.current || 0;
      const waitingTime = logThrottleDelay - (Date.now() - lastLogTime);

      if (waitingTime > 0) {
        if (logUpdateTimerRef.current) {
          return;
        }

        logUpdateTimerRef.current = setTimeout(() => {
          const newRecords = [...logRecordsRef.current];
          setConnectionLogRecords(newRecords);
          lastLogTimeRef.current = Date.now();
          logUpdateTimerRef.current = null;
        }, waitingTime);

        return;
      }
      if (logUpdateTimerRef.current) {
        clearTimeout(logUpdateTimerRef.current);
        logUpdateTimerRef.current = null;
      }
      lastLogTimeRef.current = Date.now();
      const newRecords = [...logRecordsRef.current];
      setConnectionLogRecords(newRecords);
    },
    [maxLogRecords, logThrottleDelay]
  );

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (logUpdateTimerRef.current) {
        clearTimeout(logUpdateTimerRef.current);
      }
    };
  }, []);

  const handleChannelMessage = (channelMessage) => {
    if (!channelMessage) return;
    // log_debug("received message from robot: ", channelMessage);
    addLogRecord("received message from robot: " + channelMessage);
    if (channelMessage.channel == "chat") {
      const data = JSON.parse(channelMessage.data);
      if ("topic" in data) {
        const topic = data.topic;
        const topic_data = data.value;
        if (topic == "/battery") {
          const battery = {
            battery_level:
              topic_data.percentage != "undefined"
                ? Math.round(topic_data.percentage * 100)
                : null,
            now_charging: topic_data.power_supply_status == 1,
          };
          const robotInfo = { ...robotStatus, battery: battery };
          setRobotStatus(robotInfo);
        }
      } else {
        const msg_type = data["msg_type"];

        if (msg_type == "robot_state") {
          const msg_body = data["msg_body"];
          let robotInfo = msg_body["common_info"];
          const errorInfo = msg_body["error_info"];
          const missionInfo = msg_body["mission_info"];
          robotInfo["error_info"] = errorInfo;
          const connectionInfo = msg_body["connection_info"];
          if (connectionInfo != null) {
            robotInfo["connection_info"] = msg_body["connection_info"];
          }
          // console.log("robotInfo: ", robotInfo);
          // TODO: check map

          setRobotErrorInfo(errorInfo);
          setRobotStatus(robotInfo);
          setMissionStatus(missionInfo);
        }
      }
    }
  };

  return (
    <div className="robot-control-body">
      <div className="status-panel">{contextHolder}</div>

      <div className="jitter-panel">{/* <JitterGraph></JitterGraph> */}</div>

      <div className="cameras-panel">
        {peerConnections &&
          peerConnections.map((peer) => {
            const enableStats = peer?.has_camera || false;
            const allowLocalAudio = peer?.has_speaker || false;
            const allowRemoteAudio = peer?.has_microphones || false;
            const allowRemoteVideo = peer?.has_camera || false;
            const isReceiveAudio = peer?.has_microphones || false;
            const isSendingAudio = peer?.has_speaker || false;
            const isControlChannel = peer?.is_control_channel || false;
            // console.log(
            //   "peer: ",
            //   peer?.id,
            //   "allowRemoteAudio: ",
            //   allowRemoteAudio,
            //   "allowRemoteVideo: ",
            //   allowRemoteVideo,
            //   " allowLocalAudio: ",
            //   allowLocalAudio
            // );
            if (peer.has_camera) {
              return (
                <RobotPeerConnectionProvider
                  key={peer.id}
                  StatsEnabled={enableStats}
                  allowLocalAudio={allowLocalAudio}
                  allowRemoteAudio={allowRemoteAudio}
                  allowRemoteVideo={allowRemoteVideo}
                  isReceiveAudio={isReceiveAudio}
                  isSendingAudio={isSendingAudio}
                  trickleICE={true}
                  disableChannelMessageState={true}
                  dataChannels={["chat"]}
                >
                  <div className="camera" key={peer.id}>
                    <RobotPeerVideoViewer
                      key={peer.id}
                      PeerID={peer.id}
                      onChannelMessage={handleChannelMessage}
                      ShowDelay={peer?.has_camera ? true : false}
                    ></RobotPeerVideoViewer>
                  </div>
                </RobotPeerConnectionProvider>
              );
            } else if (peer.has_microphones || peer.has_speaker) {
              return (
                <RobotPeerConnectionProvider
                  key={peer.id}
                  StatsEnabled={enableStats}
                  allowLocalAudio={allowLocalAudio}
                  allowRemoteAudio={allowRemoteAudio}
                  allowRemoteVideo={allowRemoteVideo}
                  isReceiveAudio={isReceiveAudio}
                  isSendingAudio={isSendingAudio}
                  trickleICE={true}
                  disableChannelMessageState={true}
                  dataChannels={["chat"]}
                >
                  <div className="hiden-camera" key={peer.id}>
                    <RobotPeerVideoViewer
                      key={peer.id}
                      PeerID={peer.id}
                      onChannelMessage={handleChannelMessage}
                      ShowDelay={peer?.has_camera ? true : false}
                    ></RobotPeerVideoViewer>
                  </div>
                </RobotPeerConnectionProvider>
              );
            } else {
              return "";
            }
          })}
      </div>

      <div className="log-panel">
        Log messages:
        <LogViewer
          LogRecords={connectionLogRecords}
          displaySize={5}
        ></LogViewer>
      </div>
    </div>
  );
};

export default RobotGroupMonitorPage;
